import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";

type Post = {
    _id: string;
    title: string;
    slug: string;
    excerpt: string;
    tags: string[];
    publishedAt: string;
    votes: number;
};

const Blog = () => {
    const [posts, setPosts] = useState<Post[]>([]);
    const [loading, setLoading] = useState(true);
    const [tagSearch, setTagSearch] = useState("");
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    useEffect(() => {
        api.getPosts().then((data) => {
            setPosts(data);
            setLoading(false);
        });
    }, []);

    const tagCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        posts.forEach((p) => p.tags.forEach((t) => { counts[t] = (counts[t] ?? 0) + 1; }));
        return counts;
    }, [posts]);

    const visibleTags = useMemo(() => {
        const q = tagSearch.trim().toLowerCase();
        return Object.entries(tagCounts)
            .filter(([tag]) => !q || tag.toLowerCase().includes(q))
            .sort((a, b) => b[1] - a[1]);
    }, [tagCounts, tagSearch]);

    const filteredPosts = useMemo(() => {
        if (selectedTags.length === 0) return posts;
        return posts.filter((p) => selectedTags.every((t) => p.tags.includes(t)));
    }, [posts, selectedTags]);

    const toggleTag = (tag: string) => {
        setSelectedTags((prev) =>
            prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
        );
    };

    return (
        <div className="min-h-screen bg-(--bg-main) px-8 py-10 max-w-3xl mx-auto">
            <header className="mb-16">
                <a href="https://sambarvadai.dev" className="font-inter text-sm text-neutral-400 hover:text-neutral-600 transition-colors">
                    ← sambarvadai.dev
                </a>
                <h1 className="font-is text-5xl mt-6">Anirudh's little corner of the internet.</h1>
                <p className="font-inter text-sm text-neutral-500 mt-2">thoughts, essays, and occasional rants.</p>
            </header>

            {!loading && posts.length > 0 && (
                <div className="mb-10">
                    <input
                        type="text"
                        placeholder="filter by tag..."
                        value={tagSearch}
                        onChange={(e) => setTagSearch(e.target.value)}
                        className="font-inter text-sm bg-transparent border-b border-neutral-200 outline-none pb-1 w-48 placeholder:text-neutral-300 text-neutral-600 mb-4"
                    />
                    {visibleTags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {visibleTags.map(([tag, count]) => {
                                const active = selectedTags.includes(tag);
                                return (
                                    <button
                                        key={tag}
                                        onClick={() => toggleTag(tag)}
                                        className={`font-inter text-xs px-3 py-0.5 rounded-full border transition-colors cursor-pointer ${
                                            active
                                                ? "border-[#F4611D] text-[#F4611D] bg-orange-50"
                                                : "border-neutral-300 text-neutral-400 hover:border-neutral-400 hover:text-neutral-600"
                                        }`}
                                    >
                                        {tag} <span className="opacity-60">{count}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {selectedTags.length > 0 && (
                        <button
                            onClick={() => setSelectedTags([])}
                            className="font-inter text-xs text-neutral-400 hover:text-neutral-600 transition-colors mt-3 cursor-pointer"
                        >
                            clear filters
                        </button>
                    )}
                </div>
            )}

            {loading ? (
                <p className="font-inter text-sm text-neutral-400">loading...</p>
            ) : filteredPosts.length === 0 ? (
                <p className="font-inter text-sm text-neutral-400">no posts found.</p>
            ) : (
                <div className="flex flex-col gap-12">
                    {filteredPosts.map((post) => (
                        <Link to={`/post/${post.slug}`} key={post._id} className="group">
                            <p className="font-inter text-xs text-neutral-400 mb-2">
                                {new Date(post.publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                            </p>
                            <h2 className="font-is text-3xl group-hover:text-[#F4611D] transition-colors duration-200">{post.title}</h2>
                            {post.excerpt && (
                                <p className="font-inter text-sm text-neutral-500 mt-2 font-light">{post.excerpt}</p>
                            )}
                            <div className="flex items-center gap-3 mt-3">
                                {post.tags.length > 0 && (
                                    <div className="flex gap-2">
                                        {post.tags.map((tag) => (
                                            <span key={tag} className="font-inter text-xs px-2 py-0.5 rounded-full border border-neutral-300 text-neutral-400">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <span className="font-inter text-xs text-neutral-400 flex items-center gap-1 ml-auto">
                                    <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor">
                                        <polygon points="10,3 19,17 1,17" />
                                    </svg>
                                    {post.votes ?? 0}
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Blog;
